import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Tag, Alert, message, Typography } from "antd";
import {
  CreditCardOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import {
  cancelSubscription,
  changeSubscription,
  startSubscriptionCheckoutFromApp,
} from "../../lib/api/subscriptionApi";
import {
  ENTERPRISE_TIER,
  getDisplayPricingLadder,
  getMarket2026DisplaySchedule,
  getPricingSchedule,
  FREE_TIER,
  openEnterpriseSalesWindow,
  SHARED_PLAN_FEATURES,
  normalizePlanKeyForSchedule,
  getRawSubscriptionPlanKey,
  isFreeTierUser,
  getAnnualDisplayUsd,
  formatAnnualDisplayUsd,
  ROI_SUBHEADER_COPY,
  COST_OF_VACANCY_TOOLTIP,
  TIER_VALUE_BADGES_2026,
} from "../../lib/pricing";
import { isGrandfatheredScaleSuite } from "../../lib/grandfathering";
import { trackEvent } from "../../helpers/mixpanel";

const { Text, Title } = Typography;
const { confirm } = Modal;

/**
 * Defer `Modal.confirm` after closing the parent manage modal so both are not
 * mount/unmounting in the same frame. Otherwise Ant can leave a full-viewport
 * `.ant-modal-wrap` / mask (z-index 10000 in globals) that blocks the app while
 * portaled widgets (e.g. Leo) stay clickable above.
 */
const SUBSCRIPTION_CHILD_CONFIRM_DELAY_MS = 360;

/** Free Gateway vs paid door bands. Every paid lane is the full LandlordOps OS. */
function planLaneForKey(planKey) {
  if (planKey === "free") return "free";
  if (planKey === "enterprise") return "enterprise";
  return "full_os";
}

function planLaneLabel(lane) {
  if (lane === "free") return "Free Gateway";
  if (lane === "full_os") return "LandlordOps OS";
  if (lane === "enterprise") return "Enterprise";
  return "LandlordOps OS";
}

const ModalContent = styled.div`
  padding: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

/** grid-cols-1 md:grid-cols-2 lg:grid-cols-4; parent modal width must fit four columns (see StyledModal). */
const PlansGrid = styled.div`
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  gap: 1rem;
  margin: 0.875rem 0 0;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 768px) {
    gap: 1.125rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    gap: 1rem;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

/* Enterprise: full-width horizontal band (aligns with home pricing sidecar). */
const ENTERPRISE_GOLD = "#c9a227";
const ENTERPRISE_GOLD_CTA_TEXT = "#3a3024";

const ModalEnterpriseSidecar = styled.div`
  --enterprise-gold: #c9a227;
  --enterprise-gold-soft: rgba(201, 162, 39, 0.45);
  --enterprise-gold-glow: rgba(212, 175, 85, 0.55);
  position: relative;
  border-radius: 16px;
  padding: 1.5rem 1.5rem;
  margin: 1rem 0 0 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: linear-gradient(
    145deg,
    #fffcf5 0%,
    rgba(255, 252, 245, 0.97) 40%,
    rgba(248, 244, 232, 0.95) 100%
  );
  border: 2px solid var(--enterprise-gold);
  box-shadow: 0 0 0 1px rgba(255, 220, 150, 0.5),
    0 0 20px var(--enterprise-gold-soft), 0 8px 32px rgba(0, 0, 0, 0.06);

  @media (max-width: 860px) {
    padding: 1.25rem 1.1rem;
  }
`;

const ModalEnterpriseSidecarInner = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.75rem;

  @media (max-width: 860px) {
    flex-direction: column;
    align-items: center;
    gap: 1.25rem;
    text-align: center;
  }
`;

const ModalEnterpriseSidecarLeft = styled.div`
  flex: 1;
  min-width: 0;
  @media (min-width: 861px) {
    text-align: left;
  }
`;

const ModalEnterpriseEyebrow = styled.p`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-primary-darker);
  font-family: var(--font-secondary);
  margin: 0 0 0.35rem;
`;

const ModalEnterpriseTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-text);
  margin: 0 0 0.25rem;
  letter-spacing: -0.02em;
`;

const ModalEnterpriseTagline = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0 0 0.85rem;
  line-height: 1.45;
`;

const ModalEnterpriseFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  @media (max-width: 860px) {
    text-align: left;
    width: 100%;
  }
`;

const ModalEnterpriseFeatureItem = styled.li`
  font-size: 0.85rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  padding-left: 1.1rem;
  position: relative;
  line-height: 1.4;
  &::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--color-primary);
    font-weight: 700;
  }
`;

const ModalEnterpriseSidecarRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  flex-shrink: 0;
  min-width: 180px;
`;

const ModalEnterpriseCTA = styled.button`
  background: linear-gradient(
    145deg,
    #e6c85c 0%,
    ${ENTERPRISE_GOLD} 52%,
    #b8921f 100%
  );
  color: ${ENTERPRISE_GOLD_CTA_TEXT};
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 0.75rem 1.35rem;
  border-radius: 10px;
  font-size: 0.98rem;
  font-weight: 700;
  font-family: var(--font-secondary);
  cursor: pointer;
  width: 100%;
  max-width: 320px;
  box-shadow: 0 4px 14px rgba(201, 162, 39, 0.32);
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
`;

const ModalEnterpriseSubtext = styled.p`
  font-size: 0.72rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  margin: 0;
  text-align: center;
  line-height: 1.45;
`;

const ModalEnterpriseSubscribedBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: var(--color-primary);
  color: #fff;
  font-family: var(--font-secondary);
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  box-shadow: 0 2px 8px var(--shadow-medium);
  @media (max-width: 860px) {
    position: static;
    margin-bottom: 0.5rem;
    align-self: center;
  }
`;

const LO_GREEN = "#2C5F2D";
const LO_MINT = "#86E49D";

const PlanCard = styled.div`
  min-width: 0;
  /* Current plan: strong green. Recommended (e.g. Scale) when not selected: softer border so it never looks "Subscribed". */
  border: 2px solid
    ${(props) =>
      props.$isCurrent
        ? LO_GREEN
        : props.$featured
        ? "rgba(44, 95, 45, 0.35)"
        : "var(--color-border-light)"};
  border-radius: 12px;
  padding: 1.75rem 1.5rem;
  background: ${(props) => {
    if (props.$isCurrent) {
      return `linear-gradient(135deg, ${LO_MINT}22 0%, ${LO_MINT}0d 100%)`;
    }
    if (props.$lane === "full_os") {
      return `linear-gradient(160deg, ${LO_GREEN}1a 0%, var(--color-bg-secondary) 60%)`;
    }
    if (props.$lane === "enterprise") {
      return `linear-gradient(160deg, ${LO_MINT}18 0%, var(--color-bg-secondary) 60%)`;
    }
    return "var(--color-bg-secondary)";
  }};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${(props) => (props.$interactive ? "pointer" : "default")};
  position: relative;
  box-shadow: ${(props) => {
    if (props.$isCurrent) return "0 4px 16px var(--shadow-medium)";
    if (props.$featured)
      return "0 6px 18px rgba(44, 95, 45, 0.12), 0 1px 4px rgba(0, 0, 0, 0.04)";
    return "0 2px 8px rgba(0, 0, 0, 0.04)";
  }};

  &:hover {
    ${(props) =>
      props.$interactive
        ? `
    border-color: ${
      props.$isCurrent ? "var(--color-primary)" : "var(--color-primary-light)"
    };
    box-shadow: ${
      props.$isCurrent
        ? "0 4px 16px var(--shadow-medium)"
        : "0 8px 24px var(--shadow-light)"
    };
    transform: ${props.$isCurrent ? "none" : "translateY(-2px)"};
    `
        : ""}
  }

  /* Lift the featured (Scale) card slightly at md+ so it's visually prominent
     but still fits in the 4-column grid at 1320px. */
  @media (min-width: 1024px) {
    transform: ${(props) =>
      props.$interactive && props.$featured ? "translateY(-6px)" : "none"};
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  min-height: 60px;
  padding-right: 4rem; /* Add padding to prevent overlap with badge */

  @media (min-width: 1201px) {
    padding-right: 3.5rem; /* Slightly less padding on desktop */
  }
`;

const PlanName = styled.h3`
  margin: 0 0 0.5rem 0;
  font-family: var(--font-primary);
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.02em;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${LO_GREEN};
  margin: 0.75rem 0 0.5rem 0;
  line-height: 1.2;
  letter-spacing: -0.03em;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const EnterprisePlanPriceNote = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.45;
  letter-spacing: 0;
  font-family: var(--font-secondary);
`;

/** Single shared list below the plan grid (avoids repeating the same bullets on every card). */
const SharedFeaturesSection = styled.div`
  margin: 0 0 1.5rem 0;
  padding: 1.25rem 1.5rem;
  background: var(--color-bg-tertiary, #f9fafb);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
`;

const SharedFeaturesTitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.75rem;
`;

/** Scale ($599): recommended full-OS tier in the modal (see PRICING_2026.scale.featured). */
const RecommendedTag = styled.span`
  display: inline-block;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  font-family: var(--font-secondary);
  background: linear-gradient(90deg, ${LO_MINT}55 0%, ${LO_GREEN}22 100%);
  color: ${LO_GREEN};
  border: 1px solid ${LO_GREEN}44;
`;

/** ROI sub-header shown once above the plans grid (see ROI_SUBHEADER_COPY). */
const RoiSubHeader = styled.div`
  margin: 0.25rem 0 0.25rem;
  padding: 0.875rem 1.125rem;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    rgba(44, 95, 45, 0.08) 0%,
    rgba(134, 228, 157, 0.18) 100%
  );
  border: 1px solid rgba(44, 95, 45, 0.18);
  font-family: var(--font-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-text);

  strong {
    color: ${LO_GREEN};
    font-weight: 700;
  }

  .roi-tooltip {
    margin-top: 0.35rem;
    font-size: 0.8125rem;
    color: var(--color-text-light);
    line-height: 1.45;
  }
`;

/** Matches homepage pricing toggle (monthly vs annual self-serve). */
const BillingIntervalRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  /* Breathing room below the ROI banner (or first block) without feeling cramped */
  margin: 1rem 0 0;
`;

const BillingIntervalHint = styled.span`
  font-size: 0.8125rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
`;

const IntervalToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 9999px;
  background: #eef2ed;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(44, 95, 45, 0.12);
`;

const IntervalButton = styled.button`
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 1.15rem;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--font-secondary, "Inter", sans-serif);
  color: #4b5563;
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  ${(p) =>
    p.$active
      ? `
    background: #2c5f2d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.3);
  `
      : `
    &:hover {
      color: #2c5f2d;
    }
  `}
`;

const AnnualIncentiveBadge = styled.span`
  display: inline-block;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: #cdaf63;
  color: #1f1b14;
  line-height: 1.2;
`;

const BilledAnnuallyText = styled.span`
  display: block;
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-weight: 500;
`;

/** Per-card "Labor Comparison" badge (Scale / Enterprise). */
const ValueBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.65rem;
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
  font-family: var(--font-secondary);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: ${LO_GREEN};
  background: rgba(134, 228, 157, 0.2);
  border: 1px solid rgba(44, 95, 45, 0.2);

  &::before {
    content: "$";
    font-weight: 800;
    opacity: 0.85;
  }
`;

const PlanLaneBadge = styled.span`
  display: inline-block;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  font-family: var(--font-secondary);
  ${(p) =>
    p.$lane === "full_os"
      ? `
    background: var(--color-primary);
    color: #fff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.25);
  `
      : p.$lane === "enterprise"
      ? `
    background: #1f2937;
    color: #fff;
    box-shadow: 0 2px 8px rgba(31, 41, 55, 0.28);
  `
      : p.$lane === "free"
      ? `
    background: rgba(44, 183, 107, 0.12);
    color: #1d8f52;
    border: 1px solid rgba(44, 183, 107, 0.3);
  `
      : `
    background: rgba(44, 95, 45, 0.1);
    color: var(--color-primary-dark);
    border: 1px solid rgba(44, 95, 45, 0.22);
  `}
`;

const PlanTagline = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  font-weight: 500;
`;

const SharedFeaturesList = styled.ul`
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 1.5rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  font-size: 0.8125rem;
  list-style: none;
  line-height: 1.45;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  li {
    position: relative;
    padding-left: 1.35rem;
    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: var(--color-primary);
      font-weight: 700;
      font-size: 0.75rem;
    }
  }
`;

const SubscribedBadge = styled(Tag)`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: var(--color-primary);
  color: var(--color-text-white);
  border: none;
  font-family: var(--font-secondary);
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  box-shadow: 0 2px 8px var(--shadow-medium);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.2;
  z-index: 10;

  .anticon {
    font-size: 0.6875rem;
  }

  /* Only adjust for desktop when 4 columns are shown */
  @media (min-width: 1201px) {
    top: 0.625rem;
    right: 0.625rem;
    padding: 0.1875rem 0.4375rem;
    font-size: 0.625rem;

    .anticon {
      font-size: 0.625rem;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 2px solid var(--color-border-light);
`;

const CancelButton = styled(Button)`
  &&& {
    color: var(--color-text) !important;
    border: none !important;
    padding: 0 !important;
    height: auto !important;
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    background: transparent !important;

    &:hover {
      color: var(--color-error) !important;
      background: transparent !important;
    }

    .anticon {
      margin-right: 0.5rem;
      color: var(--color-text) !important;
    }

    &:hover .anticon {
      color: var(--color-error) !important;
    }

    span {
      color: var(--color-text) !important;
    }

    &:hover span {
      color: var(--color-error) !important;
    }
  }
`;

const CancellationAlert = styled(Alert)`
  margin-bottom: 1.5rem;
  border-radius: 8px;
  font-family: var(--font-secondary);

  .ant-alert-message {
    font-weight: 600;
    color: var(--color-text);
  }

  .ant-alert-description {
    color: var(--color-text-light);
  }

  /* Mobile: keep title + body readable; put Reactivate on its own row below copy */
  @media (max-width: 768px) {
    &.ant-alert {
      flex-wrap: wrap;
      align-items: flex-start;
    }

    .ant-alert-content {
      flex: 1 1 0;
      min-width: 0;
    }

    .ant-alert-action {
      flex: 1 0 100%;
      width: 100%;
      max-width: 100%;
      margin-inline-start: 0 !important;
      margin-inline-end: 0 !important;
      margin-top: 0.75rem;
      display: flex;
      justify-content: stretch;
    }

    .ant-alert-action .ant-btn {
      width: 100%;
    }
  }
`;

const StyledModal = styled(Modal)`
  /* Fit four columns on ~1440px desktops without horizontal scroll */
  && .ant-modal {
    max-width: min(1320px, calc(100vw - 48px)) !important;
    width: min(1320px, calc(100vw - 48px)) !important;
    /* Stay within viewport: globals set wrap padding ~40px; inner scroll, not a taller-than-screen dialog */
    max-height: calc(100vh - 40px) !important;
    max-height: calc(100dvh - 40px) !important;
    margin: 0 auto !important;
    padding-bottom: 0;
    display: flex !important;
    flex-direction: column !important;
    min-height: 0 !important;
  }

  .ant-modal-content {
    border-radius: 12px;
    /* Override globals (max-height: none / overflow: visible) so body can scroll */
    max-height: calc(100vh - 40px) !important;
    max-height: calc(100dvh - 40px) !important;
    overflow: hidden !important;
    max-width: 100%;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
    display: flex !important;
    flex-direction: column !important;
    min-height: 0 !important;
  }

  .ant-modal-header {
    background: linear-gradient(
      135deg,
      var(--color-primary-darker) 0%,
      var(--color-primary-dark) 100%
    );
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 1.5rem 2rem;
    margin: 0;
  }

  .ant-modal-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
    text-align: center;
  }

  .ant-modal-close {
    z-index: 2;
    color: white !important;
    font-size: 18px;
    top: 16px;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: 36px !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: white !important;
      fill: white !important;
    }
  }

  .ant-modal-body {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 0 !important;
    flex: 1 1 auto !important;
    padding: 1.5rem 2rem 1.75rem;
    background: var(--color-bg-secondary);
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  }
`;

/**
 * Subscription Management Modal
 *
 * Shows all subscription tiers with current plan highlighted.
 * Allows users to upgrade/downgrade or cancel subscription.
 */
const SubscriptionModal = ({
  visible,
  onCancel,
  onResume,
  userInfo,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const childConfirmOpenTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (childConfirmOpenTimerRef.current != null) {
        clearTimeout(childConfirmOpenTimerRef.current);
        childConfirmOpenTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (visible && childConfirmOpenTimerRef.current != null) {
      clearTimeout(childConfirmOpenTimerRef.current);
      childConfirmOpenTimerRef.current = null;
    }
  }, [visible]);

  const subscriptionStatus = userInfo?.subscriptionStatus;
  const pricingTierGroup = userInfo?.pricingTierGroup || "market_2026";
  const rawPlanKey = getRawSubscriptionPlanKey(userInfo) || "starter";
  const currentPlanKey = normalizePlanKeyForSchedule(
    rawPlanKey,
    pricingTierGroup
  );
  /** Stripe / profile value (monthly | annual). Toggled state in this modal is synced on open. */
  const resolvedSubscriptionBilling = (() => {
    const b = String(userInfo?.billingCycle || "").toLowerCase();
    if (b === "annual" || b === "year" || b === "yearly") return "annual";
    if (b === "month" || b === "monthly") return "monthly";
    return "monthly";
  })();
  /** Billing catalog (paid Stripe-backed tiers only; used for checkout logic). */
  const billingSchedule = getPricingSchedule(pricingTierGroup);
  /**
   * Full display ladder: Free + paid (Stripe-backed) + Enterprise (quote-based).
   * Rendered in the grid. Free and Enterprise are NEVER sent through Stripe.
   */
  const activePricing = getDisplayPricingLadder(pricingTierGroup);
  const paidPricing = billingSchedule;
  const cancelAtPeriodEnd = userInfo?.subscriptionCancelAtPeriodEnd;
  const isActive = subscriptionStatus === "active";
  const isTrialing = subscriptionStatus === "trialing";
  const isCanceled = subscriptionStatus === "canceled";
  const isScheduledForCancellation = isActive && cancelAtPeriodEnd;
  /** Active paid access: show cancel; trialing users should also see cancel. */
  const canCancelSubscription =
    (isActive || isTrialing) && !isScheduledForCancellation && !isCanceled;

  const currentPlan = activePricing[currentPlanKey] || activePricing.starter;
  /**
   * Column order: Free (PLG wedge, far left) → Stripe-backed paid tiers →
   * Enterprise (quote-based, far right).
   */
  const planKeyOrder =
    pricingTierGroup === "legacy_2025"
      ? ["free", "starter", "pro", "growth", "scale"]
      : ["free", "starter", "growth", "scale", "enterprise"];
  const allPlans = planKeyOrder
    .map((key) => [key, activePricing[key]])
    .filter(([, plan]) => Boolean(plan));
  const displayPlans = allPlans;
  const gridPlans = displayPlans.filter(([k]) => k !== "enterprise");
  const enterpriseEntry =
    displayPlans.find(([k]) => k === "enterprise") || null;

  const isGrandfathered = isGrandfatheredScaleSuite(userInfo);
  /** Stale `grandfatheredScaleSuite` flag must not show a Scale banner for Free (or any non-grandfathered paid) users. */
  const showGrandfatheredBanner = isGrandfathered && !isFreeTierUser(userInfo);

  useEffect(() => {
    if (!visible) return;
    setLoading(false);
    setCancelling(false);
    const b = String(userInfo?.billingCycle || "").toLowerCase();
    const next =
      b === "annual" || b === "year" || b === "yearly" ? "annual" : "monthly";
    setBillingInterval(next);
  }, [visible, userInfo]);

  /** Close Manage Subscription first, then show a portaled confirm; reopen parent when confirm unmounts. */
  const showChildConfirm = useCallback(
    (config, options) => {
      const noResume = options?.noResume === true;
      const userAfterClose = config.afterClose;
      onCancel();
      if (childConfirmOpenTimerRef.current != null) {
        clearTimeout(childConfirmOpenTimerRef.current);
      }
      childConfirmOpenTimerRef.current = window.setTimeout(() => {
        childConfirmOpenTimerRef.current = null;
        const w =
          typeof window !== "undefined"
            ? Math.min(400, window.innerWidth - 32)
            : 400;
        Modal.confirm({
          centered: true,
          getContainer: () => document.body,
          icon: null,
          maskClosable: true,
          zIndex: 10050,
          width: w,
          wrapClassName: "lo-subscription-child-confirm",
          ...config,
          afterClose: () => {
            if (!noResume) onResume?.();
            userAfterClose?.();
          },
        });
      }, SUBSCRIPTION_CHILD_CONFIRM_DELAY_MS);
    },
    [onCancel, onResume]
  );

  const newPriceBlock = (targetPlanKey, targetPlan) => {
    if (
      !targetPlan ||
      targetPlan.type === "contact" ||
      targetPlan.monthlyUSD === 0
    ) {
      return null;
    }
    const isAnnual = billingInterval === "annual";
    const annualUsd = getAnnualDisplayUsd(targetPlanKey, pricingTierGroup);
    if (isAnnual && annualUsd != null) {
      return (
        <div
          style={{
            background: "var(--color-bg-tertiary)",
            padding: "1rem",
            borderRadius: "8px",
            marginBottom: "1rem",
            border: "1px solid var(--color-border-light)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              marginBottom: "0.5rem",
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              fontWeight: 500,
            }}
          >
            New price (annual)
          </div>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "var(--color-primary)",
            }}
          >
            ${formatAnnualDisplayUsd(annualUsd)}
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "var(--color-text-light)",
                marginLeft: "0.25rem",
              }}
            >
              /year
            </span>
          </div>
          <div
            style={{
              marginTop: "0.35rem",
              fontSize: "0.8125rem",
              color: "var(--color-text-muted)",
            }}
          >
            Billed annually
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          background: "var(--color-bg-tertiary)",
          padding: "1rem",
          borderRadius: "8px",
          marginBottom: "1rem",
          border: "1px solid var(--color-border-light)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            marginBottom: "0.5rem",
            fontSize: "0.875rem",
            color: "var(--color-text-light)",
            fontWeight: 500,
          }}
        >
          New monthly price
        </div>
        <div
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "var(--color-primary)",
          }}
        >
          ${targetPlan.monthlyUSD}
          <span
            style={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "var(--color-text-light)",
              marginLeft: "0.25rem",
            }}
          >
            /month
          </span>
        </div>
      </div>
    );
  };

  const confirmBillingIntervalChange = (targetPlanKey, targetPlan) => {
    const toAnnual = billingInterval === "annual";
    const titleText = toAnnual
      ? "Switch to annual billing?"
      : "Switch to monthly billing?";
    showChildConfirm({
      title: null,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              fontFamily: "var(--font-primary)",
              marginBottom: "1rem",
              color: "var(--color-text)",
              lineHeight: 1.3,
            }}
          >
            {titleText}
          </div>
          <p
            style={{
              marginBottom: "1rem",
              lineHeight: 1.6,
              fontSize: "0.9375rem",
            }}
          >
            {toAnnual
              ? "You'll be charged the annual rate on your next billing date (about two months free vs paying monthly)."
              : "Your subscription will move to month-to-month pricing on your next billing cycle."}
          </p>
          {newPriceBlock(targetPlanKey, targetPlan)}
        </div>
      ),
      okText: toAnnual ? "Yes, switch to annual" : "Yes, switch to monthly",
      okType: "primary",
      cancelText: "Not now",
      onOk: async () => {
        try {
          setLoading(true);
          trackEvent("subscription_billing_interval_change_started", {
            plan: targetPlanKey,
            to_billing: billingInterval,
          });
          await changeSubscription(targetPlanKey, billingInterval);
          message.success(
            toAnnual
              ? "Your plan is set to annual billing."
              : "Your plan is set to monthly billing."
          );
          trackEvent("subscription_billing_interval_change_completed", {
            plan: targetPlanKey,
            to_billing: billingInterval,
          });
          if (onUpdate) onUpdate();
          onCancel();
        } catch (error) {
          console.error("Error updating billing cycle:", error);
          message.error(
            error.message || "Failed to update billing. Please try again."
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const isUpgrade = (targetPlanKey) => {
    const currentIndex = planKeyOrder.indexOf(currentPlanKey);
    const targetIndex = planKeyOrder.indexOf(targetPlanKey);
    return targetIndex > currentIndex;
  };

  /**
   * Start-for-Free: open home intake with Free preselected; account is created
   * in the intake modal (no Stripe, no separate signup modal step).
   */
  const handleStartForFree = () => {
    trackEvent("free_gateway_cta_clicked", {
      source: "subscription_modal",
      has_active_subscription: Boolean(isActive || isTrialing),
    });
    if (isActive || isTrialing) {
      message.info(
        "You're on a paid plan. Cancel or downgrade here. The Free Gateway is for new accounts."
      );
      return;
    }
    onCancel();
    navigate("/", { state: { openFreeIntake: true } });
  };

  /**
   * Enterprise is quote-based: no Stripe SKU. Route the CTA via
   * {@link openEnterpriseSalesWindow} (defaults to `/contact`; override with
   * `REACT_APP_ENTERPRISE_CTA_HREF`).
   */
  const handleScheduleEnterprise = () => {
    trackEvent("enterprise_cta_clicked", {
      source: "subscription_modal",
      has_active_subscription: Boolean(isActive || isTrialing),
    });
    openEnterpriseSalesWindow();
  };

  const handleChangePlan = async (targetPlanKey) => {
    if (targetPlanKey === "free") {
      handleStartForFree();
      return;
    }
    if (targetPlanKey === "enterprise") {
      handleScheduleEnterprise();
      return;
    }
    if (targetPlanKey === currentPlanKey) {
      const canSwitchIntervalOnly =
        (isActive || isTrialing) &&
        !["free", "enterprise"].includes(targetPlanKey) &&
        resolvedSubscriptionBilling !== billingInterval;
      if (canSwitchIntervalOnly) {
        const planForRow =
          billingSchedule[targetPlanKey] ||
          (pricingTierGroup === "market_2026"
            ? getMarket2026DisplaySchedule()[targetPlanKey]
            : undefined);
        if (planForRow) {
          confirmBillingIntervalChange(targetPlanKey, planForRow);
        } else {
          message.error("Could not update billing for this plan.");
        }
        return;
      }
      message.info("This is your current plan");
      return;
    }

    const targetPlan =
      billingSchedule[targetPlanKey] ||
      (pricingTierGroup === "market_2026"
        ? getMarket2026DisplaySchedule()[targetPlanKey]
        : undefined);
    if (!targetPlan) {
      message.error("That plan is not available for self-serve changes.");
      return;
    }
    const isUpgradeAction = isUpgrade(targetPlanKey);
    const actionText = isUpgradeAction ? "upgrade" : "downgrade";

    /**
     * Free Gateway has no Stripe subscription: /subscriptions/change cannot run.
     * Use the same Checkout session as new signups, but the logged-in user + landlordId
     * so the webhook links the sub to this Firebase user without rotating the password.
     */
    if (isFreeTierUser(userInfo)) {
      showChildConfirm(
        {
          title: null,
          content: (
            <div
              style={{
                fontFamily: "var(--font-secondary)",
                color: "var(--color-text)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  fontFamily: "var(--font-primary)",
                  marginBottom: "1rem",
                  color: "var(--color-text)",
                  lineHeight: 1.3,
                }}
              >
                Subscribe to {targetPlan.name}?
              </div>
              <p
                style={{
                  marginBottom: "1rem",
                  lineHeight: 1.6,
                  fontSize: "0.9375rem",
                }}
              >
                You&rsquo;ll keep this same account. Next step is secure Stripe
                checkout to add a subscription. We won&rsquo;t create a second
                account or new password.
              </p>
              {newPriceBlock(targetPlanKey, targetPlan)}
              <p
                style={{
                  marginTop: "1rem",
                  marginBottom: 0,
                  lineHeight: 1.6,
                  color: "var(--color-text-light)",
                  fontSize: "0.875rem",
                }}
              >
                After you pay, you&rsquo;ll return to the app on your new plan.
              </p>
            </div>
          ),
          okText: "Continue to secure checkout",
          onOk: async () => {
            try {
              trackEvent("free_to_paid_checkout_redirect", {
                to_plan: targetPlanKey,
                billing_cycle: billingInterval,
                source: "subscription_modal",
              });
              const displayName = [userInfo?.firstName, userInfo?.lastName]
                .filter(Boolean)
                .join(" ")
                .trim();
              await startSubscriptionCheckoutFromApp({
                targetPlanKey,
                billingCycle: billingInterval,
                email: userInfo?.email,
                clientName: displayName || userInfo?.displayName,
                phone: userInfo?.phone,
                portfolioSize: userInfo?.portfolioSize,
                pricingTierGroup,
              });
            } catch (err) {
              message.error(
                err?.message || "Could not start checkout. Please try again."
              );
            }
          },
        },
        { noResume: true }
      );
      return;
    }

    // Grandfathered downgrade guard: warn before they lose Scale Suite benefits
    const isGrandfatheredDowngrade =
      !isUpgradeAction &&
      ((isGrandfathered && !isFreeTierUser(userInfo)) ||
        pricingTierGroup === "legacy_2025");

    showChildConfirm({
      title: null,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              fontFamily: "var(--font-primary)",
              marginBottom: "1rem",
              color: "var(--color-text)",
              lineHeight: 1.3,
            }}
          >
            {`${isUpgradeAction ? "Upgrade" : "Downgrade"} to ${
              targetPlan.name
            }?`}
          </div>
          {isGrandfatheredDowngrade && (
            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #f97316",
                borderRadius: "8px",
                padding: "0.875rem 1rem",
                marginBottom: "1rem",
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
                textAlign: "left",
              }}
            >
              <span style={{ fontSize: "1.1rem" }}>⚠️</span>
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#7c2d12",
                  lineHeight: 1.5,
                }}
              >
                <strong>Grandfathered pricing alert.</strong> Changing your plan
                will permanently remove your grandfathered pricing and Scale
                Suite benefits. You will be billed at current market rates and
                will not be able to reclaim legacy pricing. If you have
                questions, please{" "}
                <a
                  href="mailto:support@landlordops.com"
                  style={{ color: "#ea580c" }}
                >
                  contact support
                </a>{" "}
                before proceeding.
              </div>
            </div>
          )}

          <p
            style={{
              marginBottom: "1rem",
              lineHeight: "1.6",
              fontSize: "0.9375rem",
              textAlign: "center",
            }}
          >
            You are about to {actionText} from{" "}
            <strong style={{ fontWeight: 600, color: "var(--color-text)" }}>
              {currentPlan.name}
            </strong>{" "}
            to{" "}
            <strong style={{ fontWeight: 600, color: "var(--color-text)" }}>
              {targetPlan.name}
            </strong>
            .
          </p>

          {newPriceBlock(targetPlanKey, targetPlan)}

          {isUpgradeAction ? (
            <div
              style={{
                background: "rgba(16, 185, 129, 0.1)",
                padding: "0.875rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                border: "1px solid rgba(16, 185, 129, 0.2)",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  color: "var(--color-success)",
                  margin: 0,
                  lineHeight: "1.5",
                  fontWeight: 500,
                  fontSize: "0.9375rem",
                }}
              >
                ✓ You'll get immediate access to all {targetPlan.name} features
              </p>
            </div>
          ) : (
            <div
              style={{
                background: "rgba(245, 158, 11, 0.1)",
                padding: "0.875rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                border: "1px solid rgba(245, 158, 11, 0.2)",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  color: "var(--color-warning)",
                  margin: 0,
                  lineHeight: "1.5",
                  fontWeight: 500,
                  fontSize: "0.9375rem",
                }}
              >
                ⚠ Your plan will change at the end of your current billing
                period
              </p>
            </div>
          )}

          <p
            style={{
              marginTop: "1rem",
              marginBottom: 0,
              lineHeight: "1.6",
              color: "var(--color-text-light)",
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            Changes will be applied to your next billing cycle.
          </p>
        </div>
      ),
      okText: `Yes, ${isUpgradeAction ? "Upgrade" : "Downgrade"}`,
      okType: isUpgradeAction ? "primary" : "default",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          trackEvent("subscription_change_started", {
            from_plan: currentPlanKey,
            to_plan: targetPlanKey,
            is_upgrade: isUpgradeAction,
            billing_cycle: billingInterval,
          });

          await changeSubscription(targetPlanKey, billingInterval);

          message.success(
            isUpgradeAction
              ? `Upgraded to ${targetPlan.name}! Changes take effect immediately.`
              : `Downgraded to ${targetPlan.name}. Changes take effect at the end of your billing period.`
          );

          trackEvent("subscription_change_completed", {
            from_plan: currentPlanKey,
            to_plan: targetPlanKey,
            is_upgrade: isUpgradeAction,
            billing_cycle: billingInterval,
          });

          if (onUpdate) {
            onUpdate();
          }
          onCancel();
        } catch (error) {
          console.error("Error changing subscription:", error);
          message.error(
            error.message ||
              `Failed to ${actionText} subscription. Please try again.`
          );
          trackEvent("subscription_change_failed", {
            from_plan: currentPlanKey,
            to_plan: targetPlanKey,
            error: error.message,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancelSubscription = () => {
    showChildConfirm({
      title: null,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              fontFamily: "var(--font-primary)",
              marginBottom: "1rem",
              color: "var(--color-text)",
            }}
          >
            Cancel subscription?
          </div>
          <div style={{ marginBottom: "1rem", textAlign: "left" }}>
            <p
              style={{
                marginBottom: "0.5rem",
                lineHeight: "1.6",
                fontSize: "0.9375rem",
              }}
            >
              Your subscription will remain active until the end of your current
              billing period, then it will be canceled.
            </p>
            <p
              style={{
                marginBottom: 0,
                lineHeight: "1.6",
                fontSize: "0.9375rem",
                color: "var(--color-text-light)",
              }}
            >
              You can reactivate it anytime before the cancellation date to
              continue using all features.
            </p>
          </div>

          <div
            style={{
              background: "rgba(245, 158, 11, 0.1)",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1rem",
              border: "1px solid rgba(245, 158, 11, 0.2)",
              textAlign: "center",
            }}
          >
            <p
              style={{
                color: "var(--color-warning)",
                fontWeight: 600,
                margin: 0,
                lineHeight: "1.5",
                fontSize: "0.9375rem",
              }}
            >
              ⚠ You will lose access to all features when the subscription ends.
            </p>
          </div>
        </div>
      ),
      okText: "Yes, Cancel Subscription",
      okType: "danger",
      cancelText: "Keep Subscription",
      onOk: async () => {
        try {
          setCancelling(true);
          trackEvent("subscription_cancellation_started", {
            plan: currentPlanKey,
          });

          // Cancel at period end (not immediately)
          await cancelSubscription(false);

          message.success(
            "Your subscription will be canceled at the end of your billing period."
          );

          trackEvent("subscription_cancellation_completed", {
            plan: currentPlanKey,
          });

          if (onUpdate) {
            onUpdate();
          }
          onCancel();
        } catch (error) {
          console.error("Error canceling subscription:", error);
          message.error(
            error.message || "Failed to cancel subscription. Please try again."
          );
          trackEvent("subscription_cancellation_failed", {
            error: error.message,
            plan: currentPlanKey,
          });
        } finally {
          setCancelling(false);
        }
      },
    });
  };

  /** Resubscribe / reactivate: land on Profile subscription section (Manage / Stripe). */
  const goToProfileSubscription = () => {
    trackEvent("subscription_reactivate_profile_nav", {
      plan: currentPlanKey,
      status: subscriptionStatus,
    });
    onCancel();
    navigate("/profile#subscription-billing");
  };

  return (
    <StyledModal
      title="Manage Subscription"
      open={visible}
      onCancel={onCancel}
      destroyOnClose
      footer={null}
      width={1320}
      centered
    >
      <ModalContent>
        {/* Cancellation Alert */}
        {isScheduledForCancellation && (
          <CancellationAlert
            message="Subscription Scheduled for Cancellation"
            description="Your subscription will be canceled at the end of your current billing period. To keep access, resubscribe from your Profile: Subscription Plan, then Manage."
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            action={
              <Button
                size="small"
                type="primary"
                onClick={goToProfileSubscription}
              >
                Resubscribe on Profile
              </Button>
            }
          />
        )}

        {isCanceled && (
          <CancellationAlert
            message="Subscription Canceled"
            description="Your subscription has been canceled. Open Profile, scroll to Subscription Plan at the bottom, and use Manage to resubscribe or update billing."
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            action={
              <Button
                size="small"
                type="primary"
                onClick={goToProfileSubscription}
              >
                Resubscribe on Profile
              </Button>
            }
          />
        )}

        {/* Scale Suite grandfathered: only when entitlement matches a paid / legacy context */}
        {showGrandfatheredBanner && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "linear-gradient(90deg, #14532d 0%, #166534 100%)",
              color: "#fff",
              borderRadius: "10px",
              padding: "0.625rem 1rem",
              marginBottom: "1rem",
              fontSize: "0.9rem",
              fontWeight: 600,
            }}
          >
            <CrownOutlined style={{ fontSize: "1.1rem" }} />
            Scale Suite Access Active (Grandfathered): your legacy pricing and
            full-platform benefits are locked in.
          </div>
        )}

        {/* ROI sub-header: framed once above the plans grid so the modal leads with value, not price. */}
        {pricingTierGroup !== "legacy_2025" && (
          <RoiSubHeader
            role="note"
            aria-label="Why LandlordOps pays for itself"
          >
            <strong>{ROI_SUBHEADER_COPY}</strong>
            <div className="roi-tooltip">{COST_OF_VACANCY_TOOLTIP}</div>
          </RoiSubHeader>
        )}

        <BillingIntervalRow>
          <IntervalToggle
            role="group"
            aria-label="Choose monthly or annual billing"
          >
            <IntervalButton
              type="button"
              $active={billingInterval === "monthly"}
              onClick={() => setBillingInterval("monthly")}
            >
              Monthly
            </IntervalButton>
            <IntervalButton
              type="button"
              $active={billingInterval === "annual"}
              onClick={() => setBillingInterval("annual")}
            >
              <span>Annual</span>
              <AnnualIncentiveBadge>2 months free</AnnualIncentiveBadge>
            </IntervalButton>
          </IntervalToggle>
          <BillingIntervalHint>
            {billingInterval === "annual"
              ? "Annual billing · save ~17%"
              : "Monthly billing · cancel anytime"}
          </BillingIntervalHint>
        </BillingIntervalRow>

        {/* All Plans Grid */}
        <PlansGrid
          $cols={gridPlans.length}
          $colsTablet={gridPlans.length <= 2 ? gridPlans.length : 2}
        >
          {gridPlans.map(([planKey, plan]) => {
            const isEnterprise = planKey === "enterprise";
            const isCurrent = planKey === currentPlanKey;
            const lane = planLaneForKey(planKey);
            const laneLabel =
              lane === "enterprise"
                ? "Ultimate"
                : lane === "free"
                ? "Free Gateway"
                : "LandlordOps OS";
            const capLabel =
              typeof plan.unitCap === "string"
                ? `Up to ${plan.unitCap} Doors`
                : `Up to ${plan.unitCap} Doors.`;
            /** Only Scale receives `featured: true` in PRICING_2026; Enterprise gets its own value-badge copy. */
            const isFeatured = Boolean(plan.featured);
            const valueBadge = TIER_VALUE_BADGES_2026[planKey];
            const annualUsd = getAnnualDisplayUsd(planKey, pricingTierGroup);
            const showAnnualOnCard =
              billingInterval === "annual" &&
              annualUsd != null &&
              plan.monthlyUSD > 0 &&
              plan.type !== "contact";
            const canClickCurrentForBilling =
              isCurrent &&
              (isActive || isTrialing) &&
              !["free", "enterprise"].includes(planKey) &&
              resolvedSubscriptionBilling !== billingInterval;
            /** Current plan is only clickable to switch monthly ↔ annual; otherwise ignore taps */
            const planCardInteractive = !isCurrent || canClickCurrentForBilling;

            return (
              <PlanCard
                key={planKey}
                $isCurrent={isCurrent}
                $interactive={planCardInteractive}
                $lane={lane}
                $featured={isFeatured && !isCurrent}
                onClick={() => {
                  if (isEnterprise) return;
                  if (isCurrent && !canClickCurrentForBilling) return;
                  handleChangePlan(planKey);
                }}
                role="button"
                tabIndex={planCardInteractive ? 0 : -1}
                aria-disabled={isCurrent && !canClickCurrentForBilling}
                onKeyDown={(e) => {
                  if (!planCardInteractive) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (isEnterprise) return;
                    handleChangePlan(planKey);
                  }
                }}
                style={isEnterprise ? { cursor: "default" } : undefined}
              >
                {isCurrent && !isCanceled && (
                  <SubscribedBadge>
                    <CheckCircleOutlined
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                    Subscribed
                  </SubscribedBadge>
                )}
                <PlanLaneBadge $lane={lane}>
                  {planLaneLabel(lane)}
                </PlanLaneBadge>
                {isFeatured && planKey === "scale" && (
                  <RecommendedTag>Recommended</RecommendedTag>
                )}
                <PlanHeader>
                  <div style={{ flex: 1 }}>
                    <PlanName>{plan.name}</PlanName>
                    {plan.tagline ? (
                      <PlanTagline>{plan.tagline}</PlanTagline>
                    ) : null}
                    <PlanPrice>
                      {plan.monthlyUSD === 0 ? (
                        "FREE"
                      ) : plan.type === "contact" ? (
                        <EnterprisePlanPriceNote>
                          {plan.startingAtLabel}
                        </EnterprisePlanPriceNote>
                      ) : showAnnualOnCard ? (
                        <>
                          ${formatAnnualDisplayUsd(annualUsd)}
                          <span
                            style={{
                              fontSize: "1rem",
                              fontWeight: 500,
                              color: "var(--color-text-light)",
                              marginLeft: "0.25rem",
                            }}
                          >
                            /yr
                          </span>
                          <BilledAnnuallyText>
                            Billed annually
                          </BilledAnnuallyText>
                        </>
                      ) : (
                        <>
                          ${plan.monthlyUSD}
                          <span
                            style={{
                              fontSize: "1rem",
                              fontWeight: 500,
                              color: "var(--color-text-light)",
                              marginLeft: "0.25rem",
                            }}
                          >
                            /mo
                          </span>
                        </>
                      )}
                    </PlanPrice>
                  </div>
                </PlanHeader>
                <Text
                  type="secondary"
                  style={{
                    fontSize: "0.875rem",
                    display: "block",
                    marginBottom: 0,
                    color: "var(--color-text-light)",
                    fontWeight: 500,
                    fontFamily: "var(--font-secondary)",
                  }}
                >
                  {capLabel}
                </Text>

                {valueBadge && (
                  <ValueBadge aria-label="Labor value comparison">
                    {valueBadge}
                  </ValueBadge>
                )}

                {planKey === "free" && !isCurrent && (
                  <Button
                    type="primary"
                    block
                    style={{
                      marginTop: "1.25rem",
                      minHeight: 56,
                      height: "auto",
                      padding: "0.8rem 1rem",
                      fontWeight: 700,
                      fontFamily: "var(--font-secondary)",
                      letterSpacing: 0.2,
                      lineHeight: 1.1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartForFree();
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0,
                      }}
                    >
                      <span>{FREE_TIER.ctaLabel}</span>
                      <span
                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.1 }}
                      >
                        {FREE_TIER.ctaSubline}
                      </span>
                    </span>
                  </Button>
                )}
                {isEnterprise && !isCurrent && (
                  <Button
                    type="primary"
                    block
                    style={{
                      marginTop: "1.25rem",
                      height: 44,
                      fontWeight: 700,
                      fontFamily: "var(--font-secondary)",
                      letterSpacing: 0.2,
                      background: "var(--gradient-primary)",
                      borderColor: "transparent",
                      boxShadow: "0 4px 18px rgba(44, 95, 45, 0.22)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleScheduleEnterprise();
                    }}
                  >
                    {ENTERPRISE_TIER.ctaLabel}
                  </Button>
                )}
              </PlanCard>
            );
          })}
        </PlansGrid>

        {enterpriseEntry
          ? (() => {
              const [planKey, plan] = enterpriseEntry;
              const isCurrent = planKey === currentPlanKey;
              const capLabel =
                typeof plan.unitCap === "string"
                  ? `Up to ${plan.unitCap} Doors`
                  : `Up to ${plan.unitCap} Doors.`;
              return (
                <ModalEnterpriseSidecar>
                  {isCurrent && !isCanceled && (
                    <ModalEnterpriseSubscribedBadge>
                      <CheckCircleOutlined
                        style={{ fontSize: "0.7rem", color: "#fff" }}
                      />
                      Subscribed
                    </ModalEnterpriseSubscribedBadge>
                  )}
                  <ModalEnterpriseSidecarInner>
                    <ModalEnterpriseSidecarLeft>
                      <ModalEnterpriseEyebrow>
                        150+ Door Operators
                      </ModalEnterpriseEyebrow>
                      <ModalEnterpriseTitle>
                        Institutional Excellence
                      </ModalEnterpriseTitle>
                      <ModalEnterpriseTagline>
                        {plan.startingAtLabel}
                      </ModalEnterpriseTagline>
                      <ModalEnterpriseFeatures>
                        {ENTERPRISE_TIER.features.map((f) => (
                          <ModalEnterpriseFeatureItem key={f}>
                            {f}
                          </ModalEnterpriseFeatureItem>
                        ))}
                      </ModalEnterpriseFeatures>
                    </ModalEnterpriseSidecarLeft>
                    <ModalEnterpriseSidecarRight>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: "0.82rem",
                          textAlign: "center",
                          margin: 0,
                        }}
                      >
                        {capLabel}
                      </Text>
                      {TIER_VALUE_BADGES_2026[planKey] && (
                        <ValueBadge aria-label="Labor value comparison">
                          {TIER_VALUE_BADGES_2026[planKey]}
                        </ValueBadge>
                      )}
                      <ModalEnterpriseCTA
                        type="button"
                        onClick={() => handleScheduleEnterprise()}
                      >
                        {ENTERPRISE_TIER.ctaLabel}
                      </ModalEnterpriseCTA>
                      <ModalEnterpriseSubtext>
                        Custom SLA · up to {ENTERPRISE_TIER.unitCap} units ·
                        white-label
                      </ModalEnterpriseSubtext>
                    </ModalEnterpriseSidecarRight>
                  </ModalEnterpriseSidecarInner>
                </ModalEnterpriseSidecar>
              );
            })()
          : null}

        {pricingTierGroup === "legacy_2025" && (
          <SharedFeaturesSection>
            <SharedFeaturesTitle>Included in every plan</SharedFeaturesTitle>
            <SharedFeaturesList>
              {SHARED_PLAN_FEATURES.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </SharedFeaturesList>
          </SharedFeaturesSection>
        )}

        {/* Cancel Subscription Button */}
        <ActionButtons>
          {canCancelSubscription && (
            <CancelButton
              type="text"
              onClick={handleCancelSubscription}
              loading={cancelling}
              icon={<CloseCircleOutlined />}
            >
              Cancel Subscription
            </CancelButton>
          )}
          {!canCancelSubscription && isFreeTierUser(userInfo) && (
            <Text
              type="secondary"
              style={{
                display: "block",
                textAlign: "center",
                fontSize: "0.875rem",
                maxWidth: "36rem",
                margin: "0 auto",
                lineHeight: 1.5,
              }}
            >
              The Free Gateway has no paid subscription to cancel. If you
              upgrade later, you can cancel that paid plan here before renewal.
            </Text>
          )}
        </ActionButtons>
      </ModalContent>
    </StyledModal>
  );
};

export default SubscriptionModal;
